import { useCallback, useMemo } from "react";
import i18n from "locales/i18n";
import {
  useMeQuery,
  useMyInvitedBookingsQuery,
  useRespondToBookingMutation,
} from "generated/graphql";
import { BookingType } from "pages/dashboard/pages/calendar/eventTypes";

const te = i18n.getFixedT(null, "estimator");
export interface QuoteBooking {
  id: number;
  type: BookingType;
  date: string; // "YYYY-MM-DD"
  fromTime: string; // "HH:mm"
  toTime: string; // "HH:mm"
  status: string; // "pending" | "confirmed"
  myStatus?: string; // the user's attendee status
  proposedDate?: string | null; // set while a reschedule request is pending
  proposedFrom?: string | null;
  proposedTo?: string | null;
}

/**
 * The user's active booking linked to a quote, if any. Cancelled bookings and
 * requests the user withdrew (declined their own attendance) don't count, so
 * after cancelling the user can book again.
 */
export const useQuoteBooking = (quoteId: number | null) => {
  const [{ data: meData }] = useMeQuery();
  const [{ data, fetching }] = useMyInvitedBookingsQuery({
    pause: quoteId == null,
  });

  const booking = useMemo((): QuoteBooking | null => {
    if (quoteId == null) {
      return null;
    }
    const email = meData?.me?.email?.toLowerCase();
    const match = (data?.myInvitedBookings ?? []).find((b) => {
      if (b.quote?.id !== quoteId || b.status === "cancelled") {
        return false;
      }
      const mine = b.attendees.find((a) => a.email.toLowerCase() === email);
      return !!mine && mine.status !== "declined";
    });
    if (!match) {
      return null;
    }
    const mine = match.attendees.find((a) => a.email.toLowerCase() === email);
    return {
      id: match.id,
      type: match.type as BookingType,
      date: match.date,
      fromTime: match.fromTime,
      toTime: match.toTime,
      status: match.status,
      myStatus: mine?.status ?? undefined,
      proposedDate: mine?.proposedDate,
      proposedFrom: mine?.proposedFrom,
      proposedTo: mine?.proposedTo,
    };
  }, [quoteId, data, meData]);

  return { booking, loading: fetching && !data };
};

export interface BookingActionResult {
  ok: boolean;
  error?: string;
}

// Reschedule / cancel the user's own booking via the existing attendee
// response mutation (the vendor is emailed either way).
export const useBookingResponse = () => {
  const [{ fetching }, respondMut] = useRespondToBookingMutation();

  const respond = useCallback(
    async (
      bookingId: number,
      response: "reschedule" | "decline",
      proposed?: { date: string; from: string; to: string },
    ): Promise<BookingActionResult> => {
      const res = await respondMut({
        bookingId,
        response,
        proposedDate: proposed?.date,
        proposedFrom: proposed?.from,
        proposedTo: proposed?.to,
      });
      const payload = res.data?.respondToBooking;
      if (res.error || (payload?.errors && payload.errors.length > 0)) {
        return {
          ok: false,
          error:
            payload?.errors?.[0]?.message ??
            res.error?.message ??
            te("booking.update_failed"),
        };
      }
      return { ok: true };
    },
    [respondMut],
  );

  return { respond, responding: fetching };
};
